import { useCallback, useMemo } from 'react';

import { useAuth } from '@/features/auth/useAuth';

/**
 * Permission checks for the active organisation.
 *
 * Reads the set the server resolved and returned from /auth/me, so the client
 * never computes permissions itself — it only mirrors the server's answer.
 * Switching organisation refetches the session, so the set follows the tenant.
 */
export function usePermissions() {
  const { permissions } = useAuth();

  const set = useMemo(() => new Set(permissions), [permissions]);

  const can = useCallback((permission: string): boolean => set.has(permission), [set]);

  const canAny = useCallback(
    (required: string[]): boolean => required.some((permission) => set.has(permission)),
    [set],
  );

  const canAll = useCallback(
    (required: string[]): boolean => required.every((permission) => set.has(permission)),
    [set],
  );

  return { permissions, can, canAny, canAll };
}
